import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AppInitializer } from "@/services/AppInitializer";
import { RenderManager } from "@/services/three/RenderManager";
import App from "@/App";
import "./styles/main.scss";
import { isMobile } from "@/utils/deviceDetection";
import {
  STORAGE_KEY_AO_ENABLED,
  STORAGE_KEY_SHADOWS_ENABLED
} from "@/constants/defaults";

// Load AO setting from localStorage or use default
function getAoEnabled(): boolean {
  const stored = localStorage.getItem(STORAGE_KEY_AO_ENABLED);
  if (stored !== null) {
    return stored === "true";
  }
  return true; // Default to enabled
}

// Load shadows setting from localStorage or use default
function getShadowsEnabled(): boolean {
  const stored = localStorage.getItem(STORAGE_KEY_SHADOWS_ENABLED);
  if (stored !== null) {
    return stored === "true";
  }
  return true; // Default to enabled
}

// Initialize the app and wait for completion before rendering
async function initializeApp() {
  try {
    // Set shadows enabled before initialization so it's applied during renderer setup
    const renderManager = RenderManager.getInstance();
    renderManager.shadowsEnabled = getShadowsEnabled();

    await AppInitializer.getInstance().initialize({
      width: window.innerWidth,
      height: window.innerHeight,
      aoEnabled: getAoEnabled(),
      isMobile: isMobile(),
      ssaa: false
    });

    // Ensure shadows setting is applied after initialization completes
    renderManager.setShadowsEnabled(getShadowsEnabled());
  } catch (error) {
    console.error("Failed to initialize application:", error);
  }
}

const root = ReactDOM.createRoot(document.getElementById("root")!);

// Start initialization and render the app when ready
initializeApp()
  .then(() => {
    root.render(
      <ErrorBoundary>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ErrorBoundary>
    );
  })
  .catch((error) => {
    console.error("App failed to initialize:", error);
    root.render(
      <div style={{ padding: 20, fontFamily: "sans-serif" }}>
        <h1>Failed to load</h1>
        <p>{String(error?.message || error)}</p>
      </div>
    );
  });
